import Calculator, {
  BikeTypeV2,
  CalculationDetails,
  FrameContractDTO,
  InputDataDTO,
  LeasingOptionType,
  PaidFor,
  ResultSet,
  ResultSetWithCalculationDetails,
} from "@jobrad-gmbh/bike-leasing-calculator";
import { AppAction, CalculationActionTypes, CalculatorEnv } from "..";
import { getBikeTypeOptions, getServiceOptions } from "../frameContractUtils";
import { CalculatorWarning, performPlausibiltyCheck } from "./warnings.ts";
import { AppState } from "../../AppState.ts";
import {
  DEFAULT_BIKE_PRICES,
  DEFAULT_GROSS_SALARY,
  HEALTH_INSURANCE_ADDITIONAL_RATE,
} from "../../../constants/businessConstants.ts";

type CalculationState = {
  calculator: Calculator | null;
  defaultInputValues: InputDataDTO;
  userInput: Partial<InputDataDTO> | null;
  result: ResultSet | null;
  calculationDetails: CalculationDetails;
  showResultsDetails: boolean;
  warnings: CalculatorWarning[];
  bikeTypeOptions: BikeTypeV2[];
  bikeServiceOptions: LeasingOptionType[];
  hasCalculated: boolean;
};

const initialCalculationState: CalculationState = {
  calculator: null,
  defaultInputValues: {
    arbeitslosenversicherungsPflichtig: true,
    kaufpreis: 0,
    uvp: 0,
    arbeitgeberZuschuss: 0,
    versicherungsZahler: "company",
    serviceType: "ivo",
    serviceSubType: false,
    serviceZahler: "company",
    gehaltBrutto: DEFAULT_GROSS_SALARY,
    steuerklasse: 1,
    fahrradtyp: "default_bike",
    kinderfreibetrag: -1,
    bundesland: "Baden-Württemberg",
    kirchensteuerPflichtig: true,
    rentenversicherungsPflichtig: true,
    krankenversicherung: "gesetzlich",
    krankenversicherungZusatzbeitrag: HEALTH_INSURANCE_ADDITIONAL_RATE,
    vorsteuerabzugsberechtigt: true,
    arbeitnehmerTyp: "angestellte",
  },
  userInput: null,
  result: null,
  calculationDetails: null,
  showResultsDetails: false,
  warnings: [],
  bikeTypeOptions: [],
  bikeServiceOptions: [],
  // will be set to true with calculation action
  hasCalculated: false,
};

export function getCalculationDetails(result: ResultSetWithCalculationDetails | null, warnings: CalculatorWarning[]) {
  if (!result) {
    return null;
  }
  if (warnings.some((w) => w.isCritical)) {
    return null;
  }

  return result.calculationDetails;
}

export const selectSubsidyDependantCalculationDetails = (state: AppState) =>
  state.calculation.calculationDetails?.hasSubsidy
    ? state.calculation.calculationDetails?.calculationDetailsWithSubsidy
    : state.calculation.calculationDetails?.calculationDetailsWithoutSubsidy;

export type EmployeePays = { employeePays?: PaidFor };
export type CompanyPays = { companyPays?: PaidFor };
export const selectPaidServices = (state: AppState): CompanyPays & EmployeePays => ({
  employeePays: state.calculation.calculationDetails?.employeePays,
  companyPays: state.calculation.calculationDetails?.companyPays,
});

const calculationReducer = (calculation: CalculationState, action: AppAction): CalculationState => {
  switch (action.type) {
    case CalculationActionTypes.INIT: {
      const bikeTypeOptions = getBikeTypeOptions(action.payload.frameContract);
      const bikeServiceOptions = getServiceOptions(action.payload.frameContract);
      const defaultInputValues = getDefaultInputValues(
        action.payload.frameContract,
        calculation.defaultInputValues,
        bikeTypeOptions,
        bikeServiceOptions,
        action.payload.calculatorEnv,
      );

      return {
        ...calculation,
        calculator: new Calculator(action.payload.frameContract),
        bikeTypeOptions,
        bikeServiceOptions,
        defaultInputValues,
      };
    }
    case CalculationActionTypes.CALCULATE: {
      if (!calculation.calculator) {
        return calculation;
      }
      const inputData = {
        ...calculation.defaultInputValues,
        ...calculation.userInput,
        ...action.payload,
      } as InputDataDTO;
      const inputDataWithDefaults = {
        ...inputData,
        arbeitgeberZuschuss: inputData.arbeitgeberZuschuss ?? 0,
        kinderfreibetrag: inputData.steuerklasse === 2 ? 1 : -1,
      } as InputDataDTO;
      const result = calculation.calculator?.calculate(inputDataWithDefaults) ?? null;
      const warnings = performPlausibiltyCheck(inputData, result);

      const calculationDetails = getCalculationDetails(result, warnings);

      return { ...calculation, result, calculationDetails, userInput: inputData, warnings, hasCalculated: true };
    }
    case CalculationActionTypes.RESET_RESULTS:
      return { ...calculation, result: null, calculationDetails: null, warnings: [] };

    case CalculationActionTypes.SHOW_RESULTS_DETAILS:
      return { ...calculation, showResultsDetails: action.payload };
    case CalculationActionTypes.CHANGE_ARBEITNEHMER_TYP: {
      const arbeitnehmerTyp = action.payload;

      if (arbeitnehmerTyp === "beamte") {
        return {
          ...calculation,
          userInput: {
            ...calculation.userInput,
            arbeitnehmerTyp,
            arbeitslosenversicherungsPflichtig: false,
            rentenversicherungsPflichtig: false,
            krankenversicherung: "privat",
          },
        };
      }
      return {
        ...calculation,
        userInput: {
          ...calculation.userInput,
          arbeitnehmerTyp,
          krankenversicherung: "gesetzlich",
          arbeitslosenversicherungsPflichtig: true,
          rentenversicherungsPflichtig: true,
        },
      };
    }
    default:
      return calculation;
  }
};

function getVersicherungszahler(payer: "company" | "employee" | "self" | "selectable") {
  if (payer === "selectable") {
    return "company";
  }
  return payer;
}

function getDefaultBikeType(frameContract: FrameContractDTO, availableBikeTypeOptions: BikeTypeV2[]) {
  if (usesOldBikeServices(frameContract)) {
    return availableBikeTypeOptions.includes("default_bike") ? "default_bike" : availableBikeTypeOptions[0];
  }

  return availableBikeTypeOptions.includes("e_bike") ? "e_bike" : availableBikeTypeOptions[0];
}

export function getDefaultBikePrice(bikeType: BikeTypeV2) {
  const defaultPriceByBikeType: Record<BikeTypeV2, number> = {
    default_bike: DEFAULT_BIKE_PRICES.DEFAULT_BIKE,
    e_bike: DEFAULT_BIKE_PRICES.E_BIKE,
    cargo_bike: DEFAULT_BIKE_PRICES.CARGO_BIKE,
  };

  return defaultPriceByBikeType[bikeType];
}

// TODO add more framecontract specific overrides here
const getDefaultInputValues = (
  frameContract: FrameContractDTO,
  defaultInputValues: InputDataDTO,
  bikeTypeOptions: BikeTypeV2[],
  bikeServiceOptions: LeasingOptionType[],
  calculatorEnv: CalculatorEnv,
): InputDataDTO => {
  const defaultBikeType = getDefaultBikeType(frameContract, bikeTypeOptions);
  const defaultBikePrice = getDefaultBikePrice(defaultBikeType);
  const derivedArbeitnehmerTyp = frameContract.calculator_type !== "beamte" ? "angestellte" : "beamte";
  if (derivedArbeitnehmerTyp === "beamte") {
    return {
      ...defaultInputValues,
      fahrradtyp: defaultBikeType,
      kaufpreis: defaultBikePrice,
      uvp: defaultBikePrice,
      serviceZahler:
        calculatorEnv === "website" || calculatorEnv === "dealer"
          ? "employee"
          : frameContract.bike_services.payer.value,
      serviceType: bikeServiceOptions.length ? bikeServiceOptions[0].service_type : "ivo",
      serviceSubType: bikeServiceOptions.length ? bikeServiceOptions[0].service_subtype : false,
      versicherungsZahler: getVersicherungszahler(frameContract.insurance_option_id.payer),
      vorsteuerabzugsberechtigt: frameContract.use_input_tax_deduction !== "no",
      rentenversicherungsPflichtig: false,
      arbeitslosenversicherungsPflichtig: false,
      arbeitnehmerTyp: "beamte",
      krankenversicherung: "privat",
    };
  } else {
    return {
      ...defaultInputValues,
      fahrradtyp: defaultBikeType,
      kaufpreis: defaultBikePrice,
      uvp: defaultBikePrice,
      serviceZahler:
        calculatorEnv === "website" || calculatorEnv === "dealer"
          ? "employee"
          : frameContract.bike_services.payer.value,
      serviceType: bikeServiceOptions.length ? bikeServiceOptions[0].service_type : "ivo",
      serviceSubType: bikeServiceOptions.length ? bikeServiceOptions[0].service_subtype : false,
      versicherungsZahler: getVersicherungszahler(frameContract.insurance_option_id.payer),
      vorsteuerabzugsberechtigt: frameContract.use_input_tax_deduction !== "no",
      rentenversicherungsPflichtig: true,
      arbeitslosenversicherungsPflichtig: true,
      arbeitnehmerTyp: "angestellte",
      krankenversicherung: "gesetzlich",
    };
  }
};

function usesOldBikeServices(frameContract: FrameContractDTO): boolean {
  return frameContract.bike_services.services_version === "1.0";
}

export { type CalculationState, calculationReducer, initialCalculationState };
